import { GetCardBillingAddressRequest, SetCardBillingAddressRequest } from '@square/dashboard-protos/models/squareup/bizbank/service';
import { GlobalAddress } from '@square/dashboard-protos/models/squareup/common/location';
import { Country } from '@square/dashboard-protos/models/squareup/common/countries';
import workflow from '@banking/ui/lib/workflow';
import getPostalCodeFormatter from '@banking/ui/utils/field-kit-formatters/postal-code';
export let BillAddressError = /*#__PURE__*/function (BillAddressError) {
  BillAddressError["None"] = "BILLING_ADDRESS_ERROR_NONE";
  BillAddressError["Generic"] = "BILLING_ADDRESS_ERROR_GENERIC";
  return BillAddressError;
}({});
export let Loading = /*#__PURE__*/function (Loading) {
  Loading["None"] = "BILLING_ADDRESS_LOADING_NONE";
  Loading["Button"] = "BILLING_ADDRESS_LOADING_BUTTON";
  Loading["Modal"] = "BILLING_ADDRESS_LOADING_MODAL";
  return Loading;
}({});
export const INITIAL_STATE = {
  billingAddress: GlobalAddress.create(),
  countryAddressData: null,
  error: BillAddressError.None,
  isOpen: false,
  loading: Loading.None,
  unitToken: ''
};
export const createState = (overrides = {}) => ({
  ...INITIAL_STATE,
  ...overrides
});
export let ActionType = /*#__PURE__*/function (ActionType) {
  ActionType["BillingAddressChange"] = "CARD_SETTINGS/BILLING_ADDRESS/BILLING_ADDRESS_CHANGE";
  ActionType["Initialize"] = "CARD_SETTINGS/BILLING_ADDRESS/INITIALIZE";
  ActionType["InitializeResponsesReceived"] = "CARD_SETTINGS/BILLING_ADDRESS/INITIALIZE_RESPONSES_RECEIVED";
  ActionType["InitializeResponsesRejected"] = "CARD_SETTINGS/BILLING_ADDRESS/INITIALIZE_RESPONSES_REJECTED";
  ActionType["SetBillingAddressReceived"] = "CARD_SETTINGS/BILLING_ADDRESS/SET_BILLING_ADDRESS_RECEIVED";
  ActionType["SetBillingAddressRejected"] = "CARD_SETTINGS/BILLING_ADDRESS/SET_BILLING_ADDRESS_REJECTED";
  ActionType["ToggleModal"] = "CARD_SETTINGS/BILLING_ADDRESS/TOGGLE_MODAL";
  ActionType["UserClickSave"] = "CARD_SETTINGS/BILLING_ADDRESS/USER_CLICK_SAVE";
  return ActionType;
}({});
export const billingAddressChangeAction = address => ({
  type: ActionType.BillingAddressChange,
  address
});
export const initializeAction = unitToken => ({
  type: ActionType.Initialize,
  unitToken
});
export const initializeResponsesReceivedAction = responses => ({
  type: ActionType.InitializeResponsesReceived,
  responses
});
export const initializeResponsesRejectedAction = error => ({
  type: ActionType.InitializeResponsesRejected,
  error
});
export const setBillingAddressReceivedAction = response => ({
  type: ActionType.SetBillingAddressReceived,
  response
});
export const setBillingAddressRejectedAction = error => ({
  type: ActionType.SetBillingAddressRejected,
  error
});
export const toggleModalAction = isOpen => ({
  type: ActionType.ToggleModal,
  isOpen
});
export const userClickSaveAction = () => ({
  type: ActionType.UserClickSave
});
function handleInitialize(context, state, action) {
  const {
    unitToken
  } = action;
  const work = () => {
    const getBillingAddressResponse = context.getCardBillingAddress(GetCardBillingAddressRequest.create({
      unitToken
    }));
    const lookupCountryAddressDataResponse = context.lookupCountryAddressData();
    return Promise.all([getBillingAddressResponse, lookupCountryAddressDataResponse]).then(initializeResponsesReceivedAction, initializeResponsesRejectedAction);
  };
  return [{
    ...state,
    isOpen: true,
    loading: Loading.Modal,
    unitToken
  }, work];
}
function handleInitializeResponsesReceived(context, state, action) {
  const [getBillingAddressResponse, countryAddressData] = action.responses;
  const billingAddress = getBillingAddressResponse.billingAddress;
  if (billingAddress == null) {
    context.sentry.captureException('[Billing address] Missing billing address');
    return [{
      ...state,
      error: BillAddressError.Generic,
      loading: Loading.None
    }, null];
  }

  // Parse the zip code comng from the backend to prevent the UI validator from flagging it as invalid,
  // if the user saves the address without changing the zip code.
  // For example, for US addresses this removes hyphens from the zip code.
  // If the backend returns a zip code with a hyphen and the user updates the address without modifying the zip code,
  // the validator will flag it as invalid. Visually, we display long US zip codes with hyphens even if the actual value,
  // does not have any hyphens.
  const formattedPostalCode = getPostalCodeFormatter(Country[countryAddressData.countryCode]).parse(billingAddress.postalCode);
  return [{
    ...state,
    billingAddress: GlobalAddress.create({
      ...billingAddress,
      postalCode: formattedPostalCode
    }),
    countryAddressData,
    loading: Loading.None
  }, null];
}
function handleInitializeResponsesRejected(context, state, action) {
  context.sentry.captureException(`[Billing address] Failed to fetch initial data - ${action.error}`);
  return [{
    ...state,
    error: BillAddressError.Generic,
    loading: Loading.None
  }, null];
}
function handleSetBillingAddressReceived(context, state, action) {
  const {
    response
  } = action;
  if (!response.success) {
    context.sentry.captureException('[Billing address] Failed to set billing address - success false');
    return [{
      ...state,
      error: BillAddressError.Generic,
      loading: Loading.None
    }, null];
  }
  return [{
    ...state,
    isOpen: false,
    loading: Loading.None
  }, null];
}
function handleSetBillingAddressRejected(context, state, action) {
  context.sentry.captureException(`[Billing address] Failed to set billing address - ${action.error}`);
  return [{
    ...state,
    error: BillAddressError.Generic,
    loading: Loading.None
  }, null];
}
function handleToggleModal(state, action) {
  return [{
    ...state,
    isOpen: action.isOpen
  }, null];
}
function handleUserClickSave(context, state) {
  const {
    unitToken,
    billingAddress
  } = state;
  const work = () => context.setCardBillingAddress(SetCardBillingAddressRequest.create({
    unitToken,
    billingAddress
  })).then(setBillingAddressReceivedAction, setBillingAddressRejectedAction);
  return [{
    ...state,
    loading: Loading.Button
  }, work];
}
export const update = context => (state, action) => {
  switch (action.type) {
    case ActionType.BillingAddressChange:
      {
        const address = action.address;
        return [{
          ...state,
          billingAddress: address
        }, null];
      }
    case ActionType.Initialize:
      return handleInitialize(context, state, action);
    case ActionType.InitializeResponsesReceived:
      return handleInitializeResponsesReceived(context, state, action);
    case ActionType.InitializeResponsesRejected:
      return handleInitializeResponsesRejected(context, state, action);
    case ActionType.SetBillingAddressReceived:
      return handleSetBillingAddressReceived(context, state, action);
    case ActionType.SetBillingAddressRejected:
      return handleSetBillingAddressRejected(context, state, action);
    case ActionType.ToggleModal:
      return handleToggleModal(state, action);
    case ActionType.UserClickSave:
      {
        return handleUserClickSave(context, state);
      }
  }
};
const billingAddress = (seed, context) => {
  const w = workflow(update(context), seed);
  return {
    states: w.states,
    updates: w.updates,
    emit: w.emit
  };
};
export default billingAddress;